import { useEffect, useState } from "react";
import { toast } from "sonner";
import {
  TrendingUp, Loader2, Copy, QrCode, Trash2, Plus, ChevronLeft, ChevronRight,
  DollarSign, Link2, Save, AlertTriangle,
} from "lucide-react";
import api, { formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { UpgradeCard } from "@/components/UpgradeCard";

const money = (n) =>
  n === null || n === undefined ? null : `$${Number(n).toLocaleString(undefined, { maximumFractionDigits: 2 })}`;

const shiftMonth = (month, by) => {
  const [y, m] = month.split("-").map(Number);
  const d = new Date(Date.UTC(y, m - 1 + by, 1));
  return `${d.getUTCFullYear()}-${String(d.getUTCMonth() + 1).padStart(2, "0")}`;
};
const monthLabel = (month) => {
  const [y, m] = month.split("-").map(Number);
  return new Date(Date.UTC(y, m - 1, 1)).toLocaleDateString(undefined, { month: "long", year: "numeric", timeZone: "UTC" });
};

const CONFIDENCE = {
  exact: "border-emerald-500/30 bg-emerald-500/10 text-emerald-600 dark:text-emerald-400",
  estimated: "border-amber-500/30 bg-amber-500/10 text-amber-600 dark:text-amber-400",
  unknown: "border-border bg-muted text-muted-foreground",
};

export function RevenueTab({ site }) {
  const siteId = site.id;
  const [data, setData] = useState(null);
  const [month, setMonth] = useState(() => new Date().toISOString().slice(0, 7));
  const [busy, setBusy] = useState("");
  const [spend, setSpend] = useState({});
  const [amount, setAmount] = useState({});
  const [link, setLink] = useState({ label: "", destination: "", source: "" });
  const [settings, setSettings] = useState({ owner_phone: "", votel_sub_account_id: "" });
  const [unmatched, setUnmatched] = useState(null);

  const load = async (m = month) => {
    try {
      const { data } = await api.get(`/sites/${siteId}/attribution`, { params: { month: m } });
      setData(data);
      setSettings({
        owner_phone: data.settings?.owner_phone || "",
        votel_sub_account_id: data.settings?.votel_sub_account_id || "",
      });
      if (data.can_manage) {
        try {
          const u = await api.get("/admin/attribution/unmatched");
          setUnmatched(u.data.count);
        } catch { /* staff-only extra, never blocks the tab */ }
      }
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Could not load the revenue report");
    }
  };
  useEffect(() => { load(month); }, [siteId, month]); // eslint-disable-line react-hooks/exhaustive-deps

  const saveSpend = async (source) => {
    const raw = spend[source];
    setBusy("spend" + source);
    try {
      const { data: res } = await api.put(`/sites/${siteId}/attribution/spend`, {
        month, source, amount: raw === "" || raw === undefined ? null : Number(raw),
      });
      setData({ ...data, report: res.report });
      setSpend({ ...spend, [source]: undefined });
      toast.success("Spend saved.");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Could not save spend");
    } finally { setBusy(""); }
  };

  const createLink = async () => {
    setBusy("link");
    try {
      const { data: res } = await api.post(`/sites/${siteId}/attribution/links`, link);
      setData({ ...data, links: res.links });
      setLink({ label: "", destination: "", source: "" });
      toast.success("Tracked link created.");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Could not create the link");
    } finally { setBusy(""); }
  };

  const deleteLink = async (code) => {
    setBusy("del" + code);
    try {
      const { data: res } = await api.delete(`/sites/${siteId}/attribution/links/${code}`);
      setData({ ...data, links: res.links });
      toast.success("Link removed.");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Could not remove the link");
    } finally { setBusy(""); }
  };

  const downloadQr = async (code) => {
    try {
      const res = await api.get(`/sites/${siteId}/attribution/links/${code}/qr.png`, { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url; a.download = `qr-${code}.png`; a.click();
      URL.revokeObjectURL(url);
    } catch { toast.error("Could not build that QR code"); }
  };

  const markWon = async (leadId) => {
    const v = amount[leadId];
    if (v === undefined || v === "" || Number(v) < 0) { toast.error("Enter what the job was worth."); return; }
    setBusy("won" + leadId);
    try {
      const { data: res } = await api.post(`/sites/${siteId}/attribution/leads/${leadId}/won`, { amount: Number(v) });
      setData({ ...data, report: res.report, leads: res.leads });
      setAmount({ ...amount, [leadId]: "" });
      toast.success(`Recorded ${money(res.won.amount)} for ${res.won.name}.`);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Could not record that job");
    } finally { setBusy(""); }
  };

  const saveSettings = async () => {
    setBusy("settings");
    try {
      await api.patch(`/sites/${siteId}/attribution/settings`, settings);
      toast.success("Saved.");
      load(month);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Could not save");
    } finally { setBusy(""); }
  };

  if (!data) return <div className="flex justify-center py-16 text-muted-foreground"><Loader2 className="h-5 w-5 animate-spin" /></div>;
  if (!data.entitled) {
    return (
      <div className="mx-auto max-w-lg space-y-3" data-testid="revenue-upgrade">
        <UpgradeCard siteId={siteId} offer={data.upgrade} />
        <p className="text-center text-xs text-muted-foreground" data-testid="revenue-standalone-note">
          {data.upgrade?.standalone_note}
        </p>
      </div>
    );
  }

  const rep = data.report;
  const t = rep.totals;

  return (
    <div className="space-y-6" data-testid="revenue-tab">
      {/* ---- header + totals ---- */}
      <div className="rounded-lg border border-border bg-card p-5">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div className="flex items-center gap-2">
            <TrendingUp className="h-4 w-4 text-primary" />
            <p className="font-display text-lg font-semibold tracking-tight">Where the money came from</p>
            {data.included_with_complete
              ? <Badge variant="outline" data-testid="revenue-included">Included with Complete</Badge>
              : <Badge variant="outline" data-testid="revenue-comped">Switched on for this site</Badge>}
          </div>
          <div className="flex items-center gap-1">
            <Button size="icon" variant="ghost" data-testid="revenue-month-prev" onClick={() => setMonth(shiftMonth(month, -1))}>
              <ChevronLeft className="h-4 w-4" />
            </Button>
            <span className="min-w-[9rem] text-center text-sm font-semibold" data-testid="revenue-month">{monthLabel(month)}</span>
            <Button size="icon" variant="ghost" data-testid="revenue-month-next" onClick={() => setMonth(shiftMonth(month, 1))}>
              <ChevronRight className="h-4 w-4" />
            </Button>
          </div>
        </div>
        <div className="mt-4 grid grid-cols-2 gap-3 md:grid-cols-6">
          {[
            ["Clicks", t.clicks, "revenue-total-clicks"],
            ["Leads", t.leads, "revenue-total-leads"],
            ["Booked", t.booked, "revenue-total-booked"],
            ["Jobs won", t.won, "revenue-total-won"],
            ["Revenue", money(t.revenue), "revenue-total-revenue"],
            ["Cost per job", t.cost_per_job === null ? "add spend" : money(t.cost_per_job), "revenue-cpj"],
          ].map(([label, value, tid]) => (
            <div key={tid} className="rounded-md border border-border bg-background px-3 py-2">
              <p className="text-xs uppercase tracking-widest text-muted-foreground">{label}</p>
              <p className="mt-0.5 font-display text-lg font-semibold" data-testid={tid}>{value}</p>
            </div>
          ))}
        </div>
        <p className="mt-3 text-xs text-muted-foreground" data-testid="revenue-note">{rep.note}</p>
        <p className="mt-2 flex items-center gap-2 text-xs text-muted-foreground" data-testid="revenue-subscription">
          <DollarSign className="h-3.5 w-3.5" />
          Beacon subscription this month: <strong>{money(rep.subscription.amount)}</strong>
          {rep.subscription.comped
            ? <Badge variant="outline" className="ml-1" data-testid="revenue-subscription-comped">comped</Badge>
            : <span>· {rep.subscription.label}</span>}
          · cost per lead {t.cost_per_lead === null ? "— add spend" : money(t.cost_per_lead)}
          <span className="sr-only" data-testid="revenue-cpl">{t.cost_per_lead === null ? "add spend" : money(t.cost_per_lead)}</span>
        </p>
      </div>

      {/* ---- per source ---- */}
      <div className="overflow-x-auto rounded-lg border border-border bg-card">
        <table className="w-full text-sm">
          <thead className="border-b border-border text-left text-xs uppercase tracking-widest text-muted-foreground">
            <tr>
              <th className="px-4 py-3">Source</th>
              <th className="px-3 py-3">Clicks</th>
              <th className="px-3 py-3">Leads</th>
              <th className="px-3 py-3">Booked</th>
              <th className="px-3 py-3">Won</th>
              <th className="px-3 py-3">Revenue</th>
              <th className="px-3 py-3">Spend this month</th>
              <th className="px-3 py-3">Cost / lead</th>
              <th className="px-3 py-3">Cost / job</th>
            </tr>
          </thead>
          <tbody data-testid="revenue-rows">
            {!rep.rows.length && (
              <tr><td colSpan={9} className="px-4 py-6 text-center text-muted-foreground" data-testid="revenue-empty">
                Nothing tracked this month yet. Create a tracked link below, or wait for the first lead.
              </td></tr>
            )}
            {rep.rows.map((row) => (
              <tr key={row.source} className="border-b border-border/60 last:border-0" data-testid={`revenue-row-${row.source}`}>
                <td className="px-4 py-3">
                  <span className="font-medium">{row.label}</span>
                  {row.unknown && (
                    <Badge variant="outline" className={`ml-2 ${CONFIDENCE.unknown}`} data-testid="revenue-unknown-badge">
                      not traced
                    </Badge>
                  )}
                  {!row.unknown && row.estimated > 0 && (
                    <span className="ml-2 text-xs text-muted-foreground" data-testid={`revenue-estimated-${row.source}`}>
                      {row.estimated} estimated
                    </span>
                  )}
                </td>
                <td className="px-3 py-3">{row.clicks}</td>
                <td className="px-3 py-3">{row.leads}</td>
                <td className="px-3 py-3">{row.booked}</td>
                <td className="px-3 py-3">{row.won}</td>
                <td className="px-3 py-3">{money(row.revenue)}</td>
                <td className="px-3 py-3">
                  {row.unknown ? <span className="text-xs text-muted-foreground">n/a</span> : (
                    <div className="flex items-center gap-1">
                      <Input className="h-8 w-24" type="number" min="0" step="1" placeholder="add spend"
                        data-testid={`revenue-spend-${row.source}`}
                        value={spend[row.source] ?? (row.spend ?? "")}
                        onChange={(e) => setSpend({ ...spend, [row.source]: e.target.value })} />
                      <Button size="icon" variant="ghost" className="h-8 w-8" disabled={busy === "spend" + row.source}
                        data-testid={`revenue-spend-save-${row.source}`} onClick={() => saveSpend(row.source)}>
                        <Save className="h-3.5 w-3.5" />
                      </Button>
                    </div>
                  )}
                </td>
                <td className="px-3 py-3" data-testid={`revenue-cpl-${row.source}`}>
                  {row.cost_per_lead === null
                    ? <span className="text-xs text-muted-foreground">{row.unknown ? "n/a" : "add spend"}</span>
                    : money(row.cost_per_lead)}
                </td>
                <td className="px-3 py-3" data-testid={`revenue-cpj-${row.source}`}>
                  {row.cost_per_job === null
                    ? <span className="text-xs text-muted-foreground">{row.unknown ? "n/a" : "add spend"}</span>
                    : money(row.cost_per_job)}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* ---- tracked links ---- */}
      <div className="rounded-lg border border-border bg-card p-5">
        <div className="flex items-center gap-2">
          <Link2 className="h-4 w-4 text-primary" />
          <p className="font-display text-lg font-semibold tracking-tight">Tracked links &amp; QR codes</p>
        </div>
        <p className="mt-1 text-sm text-muted-foreground">
          One link per place you advertise — postcards, truck decals, yard signs, your Google profile.
          Anyone who taps it is credited to that source for {data.click_window_days} days.
        </p>
        <div className="mt-4 grid gap-2 md:grid-cols-[1fr_1.4fr_auto]">
          <Input placeholder="Name, e.g. Spring postcards" data-testid="link-label"
            value={link.label} onChange={(e) => setLink({ ...link, label: e.target.value })} />
          <Input placeholder="https://yourbusiness.com/book" data-testid="link-destination"
            value={link.destination} onChange={(e) => setLink({ ...link, destination: e.target.value })} />
          <Button disabled={busy === "link"} data-testid="link-create" onClick={createLink}>
            <Plus className="mr-1.5 h-3.5 w-3.5" /> Create link
          </Button>
        </div>
        {!data.links?.length ? (
          <p className="mt-4 text-sm text-muted-foreground" data-testid="link-empty">No tracked links yet.</p>
        ) : (
          <div className="mt-4 space-y-2" data-testid="link-list">
            {data.links.map((l) => (
              <div key={l.code} className="flex flex-wrap items-center justify-between gap-2 rounded-md border border-border bg-background px-3 py-2"
                data-testid={`link-row-${l.code}`}>
                <div className="min-w-0">
                  <p className="text-sm font-medium">{l.label} <span className="text-xs text-muted-foreground">· {l.source}</span></p>
                  <code className="block break-all text-xs text-muted-foreground" data-testid={`link-url-${l.code}`}>{l.url}</code>
                </div>
                <div className="flex items-center gap-1.5">
                  <span className="text-xs text-muted-foreground" data-testid={`link-stats-${l.code}`}>
                    {l.clicks} clicks · {l.leads} leads
                  </span>
                  <Button size="sm" variant="outline" data-testid={`link-copy-${l.code}`}
                    onClick={() => { navigator.clipboard.writeText(l.url); toast.success("Link copied."); }}>
                    <Copy className="h-3.5 w-3.5" />
                  </Button>
                  <Button size="sm" variant="outline" data-testid={`link-qr-${l.code}`} onClick={() => downloadQr(l.code)}>
                    <QrCode className="h-3.5 w-3.5" />
                  </Button>
                  <Button size="sm" variant="ghost" disabled={busy === "del" + l.code}
                    data-testid={`link-delete-${l.code}`} onClick={() => deleteLink(l.code)}>
                    <Trash2 className="h-3.5 w-3.5" />
                  </Button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* ---- open jobs ---- */}
      <div className="rounded-lg border border-border bg-card p-5">
        <p className="font-display text-lg font-semibold tracking-tight">Open jobs</p>
        <p className="mt-1 text-sm text-muted-foreground">
          Put the job value in and we'll credit it to the source that brought the lead.
          {data.settings?.owner_phone
            ? ` You can also text “WON 850” from ${data.settings.owner_phone}.`
            : " Add your mobile below and you'll be able to text “WON 850” instead."}
          {!data.sms_provider_live && " (Texting goes live once the SMS provider is wired.)"}
        </p>
        {!data.leads?.open?.length ? (
          <p className="mt-4 text-sm text-muted-foreground" data-testid="revenue-open-empty">No open jobs right now.</p>
        ) : (
          <div className="mt-4 space-y-2" data-testid="revenue-open-list">
            {data.leads.open.map((l) => (
              <div key={l.id} className="flex flex-wrap items-center justify-between gap-2 rounded-md border border-border bg-background px-3 py-2"
                data-testid={`lead-row-${l.id}`}>
                <div>
                  <p className="text-sm font-medium">{l.name} <span className="text-xs text-muted-foreground">· {l.phone}</span></p>
                  <p className="text-xs text-muted-foreground">
                    {l.source_label}
                    <span className={`ml-2 rounded-full border px-1.5 py-0.5 ${CONFIDENCE[l.confidence]}`}
                      data-testid={`lead-confidence-${l.id}`}>{l.confidence}</span>
                  </p>
                </div>
                <div className="flex items-center gap-1.5">
                  <Input className="h-8 w-28" type="number" min="0" step="1" placeholder="Job value"
                    data-testid={`lead-amount-${l.id}`} value={amount[l.id] ?? ""}
                    onChange={(e) => setAmount({ ...amount, [l.id]: e.target.value })} />
                  <Button size="sm" disabled={busy === "won" + l.id} data-testid={`lead-won-${l.id}`}
                    onClick={() => markWon(l.id)}>Mark won</Button>
                </div>
              </div>
            ))}
          </div>
        )}
        {!!data.leads?.won?.length && (
          <div className="mt-4 border-t border-border pt-3" data-testid="revenue-won-list">
            <p className="text-xs font-semibold uppercase tracking-widest text-muted-foreground">Recently won</p>
            {data.leads.won.slice(0, 6).map((l) => (
              <p key={l.id} className="mt-1 text-sm" data-testid={`won-row-${l.id}`}>
                {l.name} · {money(l.amount) || "no value recorded"} · {l.source_label}
              </p>
            ))}
          </div>
        )}
      </div>

      {/* ---- plumbing ---- */}
      <div className="rounded-lg border border-border bg-card p-5">
        <p className="font-display text-lg font-semibold tracking-tight">Lead sources &amp; CRM</p>
        <div className="mt-4 grid gap-3 md:grid-cols-2">
          <div>
            <label className="text-xs font-semibold uppercase tracking-widest text-muted-foreground">Owner mobile (for “WON 850”)</label>
            <Input className="mt-1" placeholder="(555) 123-4567" data-testid="attr-owner-phone"
              value={settings.owner_phone} onChange={(e) => setSettings({ ...settings, owner_phone: e.target.value })} />
            <p className="mt-1 text-xs text-muted-foreground">Only texts from this number are accepted. Anything else is ignored.</p>
          </div>
          <div>
            <label className="text-xs font-semibold uppercase tracking-widest text-muted-foreground">LocalPro CRM sub-account id</label>
            <Input className="mt-1" placeholder="acct_…" data-testid="attr-sub-account"
              value={settings.votel_sub_account_id} onChange={(e) => setSettings({ ...settings, votel_sub_account_id: e.target.value })} />
            <p className="mt-1 text-xs text-muted-foreground">
              Booked and won jobs arrive on this id. Webhook {data.webhook_configured ? "is configured" : "secret is not set on this server yet"}.
            </p>
          </div>
        </div>
        <Button size="sm" className="mt-3" disabled={busy === "settings"} data-testid="attr-save-settings" onClick={saveSettings}>
          <Save className="mr-1.5 h-3.5 w-3.5" /> Save
        </Button>
        {data.can_manage && unmatched !== null && (
          <p className="mt-4 flex items-center gap-2 text-xs text-muted-foreground" data-testid="revenue-unmatched">
            {unmatched > 0 && <AlertTriangle className="h-3.5 w-3.5 text-amber-500" />}
            {unmatched} CRM deliveries kept aside because they matched no site — nothing is ever dropped.
          </p>
        )}
      </div>
    </div>
  );
}
